// pages/Home.jsx
//
// Landing page - anyone can browse, no login needed. Only "Buy Now"
// (inside ProductCard) triggers the login check.
//
// BannerDisplay sits at the very top, above the hero text - it fetches
// its own data independently and renders nothing if there are no
// active banners, so it never affects layout when unused.

import React, { useState, useEffect, useMemo } from 'react';
import ProductCard from '../components/ProductCard';
import BannerDisplay from '../components/BannerDisplay';

import { API_BASE } from '../config';

function Home() {
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, settingsRes] = await Promise.all([
          fetch(`${API_BASE}/products`),
          fetch(`${API_BASE}/settings`),
        ]);
        setProducts(await productsRes.json());
        setSettings(await settingsRes.json());
      } catch (err) {
        setError('Failed to load products. Please refresh the page.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const categories = useMemo(() => {
    const unique = new Set(products.map((p) => p.category || 'General'));
    return ['all', ...Array.from(unique)];
  }, [products]);

  const filteredProducts = useMemo(() => {
    const filtered = products.filter((product) => {
      const matchesSearch =
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory =
        selectedCategory === 'all' || (product.category || 'General') === selectedCategory;
      return matchesSearch && matchesCategory;
    });

    const FEATURED_NAMES = ['netflix', 'amazon prime video'];

    return [...filtered].sort((a, b) => {
      const aFeatured = FEATURED_NAMES.includes(a.name.toLowerCase());
      const bFeatured = FEATURED_NAMES.includes(b.name.toLowerCase());
      if (aFeatured && !bFeatured) return -1;
      if (!aFeatured && bFeatured) return 1;
      if (aFeatured && bFeatured) {
        return FEATURED_NAMES.indexOf(a.name.toLowerCase()) - FEATURED_NAMES.indexOf(b.name.toLowerCase());
      }
      return new Date(b.createdAt) - new Date(a.createdAt);
    });
  }, [products, searchTerm, selectedCategory]);

  if (loading) return <p className="page muted" style={{ textAlign: 'center', marginTop: '50px' }}>Loading products...</p>;
  if (error) return <p className="page" style={{ textAlign: 'center', color: '#ff4757', marginTop: '50px' }}>{error}</p>;

  return (
    <div>
      <BannerDisplay />

      <div style={{ textAlign: 'center', padding: '50px 20px 30px' }}>
        {settings?.siteLogo && (
          <img src={settings.siteLogo} alt="Logo" style={{ height: '50px', marginBottom: '10px' }} />
        )}
        <div className="eyebrow" style={{ marginBottom: '10px' }}>// PREMIUM ACCOUNTS · INSTANT ACCESS</div>
        <h1 className="display" style={{ fontSize: '42px', margin: '0 0 12px' }}>
          {settings?.siteName || 'STREAM MORE. PAY LESS.'}
        </h1>
        <p className="muted" style={{ maxWidth: '420px', margin: '0 auto' }}>
          Browse our OTT accounts — login required only at checkout.
        </p>
      </div>

      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center', marginBottom: '30px', padding: '0 20px' }}>
        <input
          type="text"
          className="input"
          placeholder="Search Netflix, Canva, Spotify..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ maxWidth: '280px' }}
        />
        <select
          className="input"
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          style={{ maxWidth: '180px' }}
        >
          {categories.map((cat) => (
            <option key={cat} value={cat}>
              {cat === 'all' ? 'All Categories' : cat}
            </option>
          ))}
        </select>
      </div>

      {filteredProducts.length === 0 ? (
        <p className="muted" style={{ textAlign: 'center' }}>
          {products.length === 0
            ? 'No products available right now. Check back soon!'
            : 'No products match your search. Try a different keyword or category.'}
        </p>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', justifyContent: 'center', padding: '0 20px 50px' }}>
          {filteredProducts.map((product) => (
            <ProductCard
              key={product._id}
              product={product}
              newBadgeWindowDays={settings?.newBadgeWindowDays}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default Home;
